import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError, USE_MOCK, userAdminApi } from '@/api/client';
import { MOCK_USERS, type AdminUser } from '@/data/mockUsers';
import type { UserItem, CreateUserApiRequest, UserModules, AdminPermissions } from '@/api/types';
import { useUserContext } from '@/contexts/UserContext';
import { VALID_ROLES, type UserRole } from '@/hooks/useCurrentUser';
import { getInitials } from '@/utils/strings';

/** Calls fn and returns fallback on 404 or network errors (service down). Re-throws other errors.
 *  Mismo patrón que safeCall en useAnalytics.ts (no se exporta desde ahí para no acoplar módulos). */
async function safeCall<T>(fn: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await fn();
  } catch (err: unknown) {
    if (err instanceof ApiError && err.status === 404) return fallback;
    if (err instanceof TypeError) return fallback; // ERR_CONNECTION_REFUSED / Failed to fetch
    throw err;
  }
}

// En modo mock (USE_MOCK) las mutaciones de este archivo se resuelven sin efecto y sin traza: la
// convención del proyecto prohíbe `console.log` en TypeScript, incluso bajo el guard de DEV.

/** Etiqueta de alcance de un usuario sin OU acotada. */
const ALL_OUS_LABEL = 'Todas';

/** Etiqueta de alcance de un administrador sin ninguna OU asignada. */
const GLOBAL_SCOPE_LABEL = 'Global';

/** Roles del backend, filtrando cualquier valor que este frontend no conozca. Un usuario sin
 *  ningún rol reconocible se trata como colaborador estándar. */
function adaptRoles(backendRoles: string[]): UserRole[] {
  const known = backendRoles.filter((role): role is UserRole => VALID_ROLES.includes(role as UserRole));
  return known.length > 0 ? known : ['Employee'];
}

/** Etiqueta del alcance de OU de un administrador: sus OUs raíz, o "Todas". */
function resolveOuScopeLabel(userItem: UserItem): string {
  if (userItem.allOusAccess) return ALL_OUS_LABEL;
  const scopeIds = userItem.pulsoAdminScopeOuIds ?? [];
  return scopeIds.length > 0 ? scopeIds.join(', ') : ALL_OUS_LABEL;
}

/** Map API UserItem (UserDto shape) → AdminUser (adds UI-only fields with defaults) */
function adaptUser(userItem: UserItem): AdminUser {
  const scopeLabel = resolveOuScopeLabel(userItem);
  return {
    id: userItem.id,
    // Revienta con los dos en null, y ocurre dentro del queryFn: tumbaba la query COMPLETA
    // (useUsers trae el roster entero, con las ~3 930 fichas sin correo), asi que OnaTab y
    // NotificationsTab se quedaban sin datos.
    name: userItem.displayName ?? userItem.email?.split('@')[0] ?? userItem.id,
    email: userItem.email,
    roles: adaptRoles(userItem.roles),
    ou: scopeLabel,
    ouScopeIds: userItem.allOusAccess ? [] : (userItem.pulsoAdminScopeOuIds ?? []),
    isAllOusScope: userItem.allOusAccess ?? false,
    alcance: scopeLabel === ALL_OUS_LABEL ? GLOBAL_SCOPE_LABEL : scopeLabel,
    canCreateSurveys:  userItem.canCreateSurveys  ?? false,
    canViewAnalytics:  userItem.canViewAnalytics  ?? false,
    canManageUsers:    userItem.canManageUsers    ?? false,
    canApproveSurveys: userItem.canApproveSurveys ?? false,
    canAccessCatalogFundamentals: userItem.canAccessCatalogFundamentals ?? false,
    canAccessCatalogOus:          userItem.canAccessCatalogOus          ?? false,
    // Todos los usuarios sincronizados desde AD tienen Pulso activo (migración 2026-07-28);
    // Brillamos se concede de forma explícita, así que su ausencia significa "no".
    canViewPulso:      userItem.canViewPulso      ?? true,
    canViewBrillamos:  userItem.canViewBrillamos  ?? false,
    active: userItem.isActive,
  };
}

/** Roles que se consideran "administrador" para la pantalla de administración de usuarios. */
const ADMIN_ROLES: UserRole[] = [
  'AdminGlobal', 'AdminPulso', 'AdminOU', 'AdminBrillamos', 'AdminOUBrillamos',
];

/** Tope de pageSize aceptado por el backend (GetUsersQueryValidator: 1–200). */
const USERS_FETCH_PAGE_SIZE = 200;

/** Recorre TODAS las páginas de GET /v1/users (opcionalmente filtrado por roles) y devuelve
 *  la lista completa. Exportado solo para pruebas unitarias. */
export async function fetchAllUserPages(
  roles?: string[], includeUnclaimed?: boolean): Promise<UserItem[]> {
  const firstPage = await userAdminApi.getUsers(
    { page: 1, pageSize: USERS_FETCH_PAGE_SIZE, roles, includeUnclaimed });
  const allItems = [...(firstPage.items ?? [])];
  const totalPages = Math.ceil(firstPage.totalCount / USERS_FETCH_PAGE_SIZE);
  for (let pageNumber = 2; pageNumber <= totalPages; pageNumber++) {
    const nextPage = await userAdminApi.getUsers(
      { page: pageNumber, pageSize: USERS_FETCH_PAGE_SIZE, roles, includeUnclaimed });
    allItems.push(...(nextPage.items ?? []));
  }
  return allItems;
}

/** Roster COMPLETO del tenant (todas las páginas, sin filtro de rol). Lo consumen vistas que
 *  asumen la lista entera: OnaTab (resolver OU/nombre por evaluatedId) y NotificationsTab
 *  (destinatarios de la notificación general). Ver E-017 en errores-conocidos.md. */
export function useUsers(includeUnclaimed = false) {
  return useQuery<AdminUser[]>({
    // 🔴 El flag va en la clave: sin él, encender el switch devolvería la lista cacheada SIN las
    // fichas sin reclamar y parecería que el switch no hace nada.
    queryKey: ['users', { includeUnclaimed }],
    queryFn: async () => {
      if (USE_MOCK) return MOCK_USERS;
      return (await fetchAllUserPages(undefined, includeUnclaimed)).map(adaptUser);
    },
  });
}

/** Lista COMPLETA de administradores del tenant (roles AdminGlobal/AdminOU), filtrada
 *  server-side para que Gestión de Administradores nunca muestre una lista parcial.
 *
 *  🔴 `includeUnclaimed: true` — aquí D-U2 no aplica y esconder no cuesta filas, cuesta control: la
 *  consulta ya viene acotada `roles=Admin*` server-side, así que solo puede devolver administradores
 *  REALES, nunca las 34 821 fichas del padrón. Desde que el buscador de alta ve el padrón completo
 *  sí se puede nombrar administrador a una ficha sin reclamar, y con el filtro puesto esa persona
 *  quedaba con el rol guardado pero INVISIBLE en la única pantalla desde la que se le puede quitar.
 *  Es justo lo que pasó en UAT: tres fichas de Bar-S quedaron como AdminGlobal sin fila que mostrar.
 */
export function useAdminUsers() {
  return useQuery<AdminUser[]>({
    queryKey: ['users', 'admins'],
    queryFn: async () => {
      if (USE_MOCK) return MOCK_USERS;
      return (await fetchAllUserPages(ADMIN_ROLES, true)).map(adaptUser);
    },
  });
}

/**
 * Bug (caché, 2026-08-18) — un cambio de rol hecho en /usuarios (Pulso) no se reflejaba en
 * Roles y Permisos (Brillamos): "Quitar administrador global" ahí es en realidad
 * `useUpdateUserRoles` degradando `Roles` a Employee, pero esta mutación solo invalidaba
 * `['users']`, sin saber que `AdminRoles.tsx` lee `['recognition', 'admins']`
 * (`GetRecognitionAdminsQueryHandler` filtra por ese mismo campo `Roles`). Con el `staleTime`
 * global de 5 min (`main.tsx`), la fila stale de "Admin General" se quedaba visible en Brillamos
 * hasta que el caché expirara solo — no con solo navegar de una pantalla a la otra. Mismo criterio
 * en las tres mutaciones de aquí que pueden sacar o meter a alguien del roster de Brillamos
 * (alta con rol AdminGlobal, cambio de roles, borrado de cuenta).
 */
function invalidateUsersAndRecognitionAdmins(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['users'] });
  void qc.invalidateQueries({ queryKey: ['recognition', 'admins'] });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateUserApiRequest) => {
      if (USE_MOCK) {
        const mockItem: UserItem = {
          id: String(Date.now()), email: body.email, displayName: body.displayName,
          tenantId: 'LATAM', roles: body.roles ?? ['AdminOU'], isActive: true,
        };
        return Promise.resolve(mockItem);
      }
      return userAdminApi.createUser(body);
    },
    onSuccess: () => invalidateUsersAndRecognitionAdmins(qc),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: { preferredLanguage?: string } }) => {
      if (USE_MOCK) {
        return Promise.resolve({ id, email: '', tenantId: '', roles: [], isActive: true, ...body });
      }
      return userAdminApi.updateUser(id, body);
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['users'] }); },
  });
}

/** Alcance de administración de un AdminOU sobre Pulso (una o varias OUs, con cascada resuelta
 *  server-side) — separado del idioma/OU de identidad, que administra useUpdateUser. */
export function useUpdateUserOuScope() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ouIds, allOusAccess }: { id: string; ouIds: string[]; allOusAccess: boolean }) => {
      if (USE_MOCK) return Promise.resolve({} as UserItem);
      return userAdminApi.updateUserOuScope(id, { ouIds, allOusAccess });
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['users'] }); },
  });
}

export function useUpdateOwnLanguage() {
  return useMutation({
    mutationFn: ({ id, preferredLanguage }: { id: string; preferredLanguage: string }) => {
      if (USE_MOCK) {
        return Promise.resolve({
          id, email: '', tenantId: '', roles: [], isActive: true, preferredLanguage,
        } as UserItem);
      }
      return userAdminApi.updateOwnLanguage(id, preferredLanguage);
    },
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (USE_MOCK) return Promise.resolve();
      return userAdminApi.deleteUser(id);
    },
    onSuccess: () => invalidateUsersAndRecognitionAdmins(qc),
  });
}

export function useToggleUserActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => {
      if (USE_MOCK) return Promise.resolve({} as UserItem);
      return userAdminApi.toggleUserActive(id, isActive);
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['users'] }); },
  });
}

export function useUpdateUserRoles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, roles }: { id: string; roles: string[] }) => {
      if (USE_MOCK) return Promise.resolve({} as UserItem);
      return userAdminApi.updateUserRoles(id, roles);
    },
    onSuccess: () => invalidateUsersAndRecognitionAdmins(qc),
  });
}

export function useUpdateUserPermissions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, perms }: {
      id: string;
      perms: AdminPermissions;
    }) => {
      if (USE_MOCK) return Promise.resolve({} as UserItem);
      return userAdminApi.updateUserPermissions(id, perms);
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['users'] }); },
  });
}

/** Módulos del producto (Pulso / Brillamos) visibles para un usuario. Eje independiente del rol:
 *  se puede administrar un módulo sin tener acceso al otro. */
export function useUpdateUserModules() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, modules }: { id: string; modules: UserModules }) => {
      if (USE_MOCK) return Promise.resolve({} as UserItem);
      return userAdminApi.updateUserModules(id, modules);
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['users'] }); },
  });
}

export interface AudiencePerson {
  id: string;
  name: string;
  initials: string;
  jobTitle: string;
  ouId: string;
}

// `initialsFromName` era otra implementación de lo mismo. Se conserva el NOMBRE como alias para no
// tocar a `adaptAudiencePerson` ni a las pruebas que ya la afirman; la lógica es única.
export const initialsFromName = getInitials;

export function adaptAudiencePerson(u: UserItem): AudiencePerson {
  // El id como ultimo recurso: sin nombre y sin correo, mostrar vacio seria peor que mostrar algo.
  const name = u.displayName ?? u.email ?? u.id;
  return {
    id: u.id,
    name,
    initials: initialsFromName(name),
    jobTitle: u.jobTitle ?? '',
    ouId: u.ouId ?? '',
  };
}

const AUDIENCE_DEFAULT_PAGE_SIZE = 10;

/** Personas reales de una OU para StepAudiencia (modo "personalizada") — reemplaza MOCK_PEOPLE (E-009). */
export function useAudiencePeople(ouId: string, search: string, page = 1, pageSize = AUDIENCE_DEFAULT_PAGE_SIZE) {
  const { demoUser } = useUserContext();
  const tenantId = demoUser.tenant;

  return useQuery<{ people: AudiencePerson[]; totalCount: number }>({
    queryKey: ['users', 'audience', tenantId, ouId, search, page, pageSize],
    enabled: !!ouId,
    queryFn: async () => {
      const res = await userAdminApi.getUsers({ page, pageSize, ouId, search });
      return { people: res.items.map(adaptAudiencePerson), totalCount: res.totalCount };
    },
  });
}

/** Resuelve displayName en lote para una lista de evaluatedId/userId — usado por el ranking
 *  generacional (GeneracionalTab) porque GET /v1/analytics/generational/participants no incluye
 *  nombre de persona por diseño (ver ParticipantOmissionDto/GenerationalParticipant en AnalyticsService). */
export function useUsersBatchLookup(userIds: string[]) {
  return useQuery<Array<{ id: string; displayName: string }>>({
    queryKey: ['users', 'batch-lookup', userIds],
    enabled: userIds.length > 0,
    retry: false,
    queryFn: () => USE_MOCK
      ? Promise.resolve([])
      : safeCall(() => userAdminApi.batchLookupUsers(userIds), []),
  });
}

export interface UserSearchResult {
  id: string;
  name: string;
  email: string | null;
}

/** Mínimo de caracteres para disparar la búsqueda de usuarios (compartido con UserSearchSelect). */
export const USER_SEARCH_MIN_CHARS = 2;

const USER_SEARCH_PAGE_SIZE = 8;

/** Búsqueda tenant-wide por nombre o correo para el autocomplete de "Agregar administrador".
 *  El debounce vive en el componente (UserSearchSelect); aquí solo se consulta el término ya estable.
 *
 *  🔴 `includeUnclaimed: true` — este es un SELECTOR, no el listado de administración. D-U2 oculta
 *  las fichas del padrón sin reclamar en el LISTADO (donde inflarían 10 496 filas a ~45 000), pero
 *  aplicado aquí dejaba el buscador ciego para el 77 % del padrón de QA: 34 821 fichas con
 *  `IsClaimed=false` frente a 10 496 reclamadas, y entre ellas TODO Bar-S/Fiorucci (EE. UU.), que
 *  nunca ha iniciado sesión. Ése era el bug de UAT: buscar `GABRIELA.TAYLOR@BAR-S.COM` —que existe
 *  en Mongo, activa y con correo— devolvía "No se encontraron usuarios". Nombrar administrador a una
 *  ficha sin reclamar es válido: el rol se guarda en la ficha y la identidad se resuelve por correo
 *  (corporativo, Azure AD) o al reclamarla por nómina (frontline), no por `IsClaimed`.
 *
 *  `isActive: true` por el mismo motivo que `GET /v1/users/search`: a quien está de baja no se le
 *  nombra administrador. Sin él, abrir el buscador al padrón entero también traería a los inactivos.
 */
export function useUserSearch(searchTerm: string) {
  const { demoUser } = useUserContext();
  const tenantId = demoUser.tenant;
  const trimmedTerm = searchTerm.trim();

  return useQuery<UserSearchResult[]>({
    queryKey: ['users', 'admin-search', tenantId, trimmedTerm],
    enabled: trimmedTerm.length >= USER_SEARCH_MIN_CHARS,
    retry: false,
    queryFn: async () => {
      const res = await userAdminApi.getUsers({
        search: trimmedTerm,
        pageSize: USER_SEARCH_PAGE_SIZE,
        includeUnclaimed: true,
        isActive: true,
      });
      return (res.items ?? []).map(item => {
        // El id como ultimo recurso: un frontline sin nombre ni correo tiene que poder buscarse.
        const name = item.displayName ?? item.email ?? item.id;
        return { id: item.id, name, email: item.email };
      });
    },
  });
}
